import React, { useState, useCallback, useEffect } from 'react';
import { Form, Button, Icon, Segment, Header, Checkbox, Dropdown, Label } from 'semantic-ui-react';

import Config from '../../../constants/Config';

const FREQUENCY_OPTIONS = [
  { key: '15m', value: '15m', text: 'Every 15 minutes' },
  { key: '30m', value: '30m', text: 'Every 30 minutes' },
  { key: '1h', value: '1h', text: 'Every hour' },
  { key: '2h', value: '2h', text: 'Every 2 hours' },
  { key: '6h', value: '6h', text: 'Every 6 hours' },
  { key: '12h', value: '12h', text: 'Every 12 hours' },
  { key: '24h', value: '24h', text: 'Once a day' },
];

const DATE_RANGE_OPTIONS = [
  { key: '1d', value: '1d', text: '1 day (yesterday to tomorrow)' },
  { key: '3d', value: '3d', text: '3 days' },
  { key: '7d', value: '7d', text: '7 days' },
  { key: '14d', value: '14d', text: '14 days' },
  { key: '30d', value: '30d', text: '30 days' },
];

const DATE_TYPE_OPTIONS = [
  { key: 'dropoff', value: 'dropoff', text: 'Dropoff Date' },
  { key: 'pickup', value: 'pickup', text: 'Pickup Date' },
  { key: 'updated', value: 'updated', text: 'Last Updated' },
];

const DUPLICATE_OPTIONS = [
  { key: 'skip', value: 'skip', text: 'Skip (do not reimport)' },
  { key: 'update', value: 'update', text: 'Update existing card' },
];

const ALL_STATUSES = [
  'Reservation',
  'Hired',
  'Confirmed',
  'Cancelled',
  'Quotation',
  'No Show',
  'Completed',
];

const EMPTY_TARGET = {
  label: '',
  targetProjectId: '',
  targetBoardId: '',
  targetListId: '',
  syncDateType: 'dropoff',
  filterLocationIds: [],
};

const RcmSyncConfig = React.memo(
  ({ config, isLoading, onConfigUpdate, onAutoSyncNow, isAutoSyncing, accessToken }) => {
    const settings = config?.settings || {};

    const [syncEnabled, setSyncEnabled] = useState(settings.syncEnabled || false);
    const [syncFrequency, setSyncFrequency] = useState(settings.syncFrequency || '1h');
    const [syncDateRange, setSyncDateRange] = useState(settings.syncDateRange || '3d');
    const [onDuplicate, setOnDuplicate] = useState(settings.onDuplicate || 'skip');
    const [allowedStatuses, setAllowedStatuses] = useState(
      settings.allowedStatuses || ['Reservation', 'Hired', 'Confirmed']
    );

    // Multi-board targets
    const [boardTargets, setBoardTargets] = useState(() => {
      if (Array.isArray(settings.boardTargets) && settings.boardTargets.length > 0) {
        return settings.boardTargets;
      }
      // Legacy migration: convert single target to array
      if (settings.targetListId) {
        return [
          {
            ...EMPTY_TARGET,
            targetProjectId: settings.targetProjectId || config?.targetProjectId || '',
            targetBoardId: settings.targetBoardId || config?.targetBoardId || '',
            targetListId: settings.targetListId || config?.targetListId || '',
            syncDateType: settings.syncDateType || 'dropoff',
            label: 'Default',
          },
        ];
      }
      return [{ ...EMPTY_TARGET }];
    });

    // Data for dropdowns (per-target)
    const [allProjects, setAllProjects] = useState([]);
    const [boardsByProject, setBoardsByProject] = useState({});
    const [listsByBoard, setListsByBoard] = useState({});
    const [rcmLocations, setRcmLocations] = useState([]);

    useEffect(() => {
      const s = config?.settings || {};
      setSyncEnabled(s.syncEnabled || false);
      setSyncFrequency(s.syncFrequency || '1h');
      setSyncDateRange(s.syncDateRange || '3d');
      setOnDuplicate(s.onDuplicate || 'skip');
      setAllowedStatuses(s.allowedStatuses || ['Reservation', 'Hired', 'Confirmed']);

      if (Array.isArray(s.boardTargets) && s.boardTargets.length > 0) {
        setBoardTargets(s.boardTargets);
      } else if (s.targetListId) {
        setBoardTargets([
          {
            ...EMPTY_TARGET,
            targetProjectId: s.targetProjectId || config?.targetProjectId || '',
            targetBoardId: s.targetBoardId || config?.targetBoardId || '',
            targetListId: s.targetListId || config?.targetListId || '',
            syncDateType: s.syncDateType || 'dropoff',
            label: 'Default',
          },
        ]);
      }
    }, [config]);

    // Fetch projects and locations on mount
    useEffect(() => {
      const fetchProjects = async () => {
        try {
          const response = await fetch(`${Config.SERVER_BASE_URL}/api/projects`, {
            headers: { Authorization: `Bearer ${accessToken}` },
            credentials: 'include',
          });
          if (response.ok) {
            const data = await response.json();
            setAllProjects(data.items || []);
          }
        } catch (err) {
          console.error('Error fetching projects:', err);
        }
      };

      const fetchLocations = async () => {
        try {
          const response = await fetch(`${Config.SERVER_BASE_URL}/api/integrations/rcm/locations`, {
            headers: { Authorization: `Bearer ${accessToken}` },
            credentials: 'include',
          });
          if (response.ok) {
            const data = await response.json();
            setRcmLocations(data.items || []);
          }
        } catch (err) {
          console.error('Error fetching RCM locations:', err);
        }
      };

      fetchProjects();
      fetchLocations();
    }, [accessToken]);

    // Pre-load boards/lists for existing targets
    useEffect(() => {
      boardTargets.forEach(target => {
        if (target.targetProjectId && !boardsByProject[target.targetProjectId]) {
          fetchBoardsForProject(target.targetProjectId);
        }
        if (target.targetBoardId && !listsByBoard[target.targetBoardId]) {
          fetchListsForBoard(target.targetBoardId);
        }
      });
    }, [boardTargets]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchBoardsForProject = useCallback(
      async projectId => {
        if (!projectId) return;
        try {
          const response = await fetch(`${Config.SERVER_BASE_URL}/api/projects/${projectId}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
            credentials: 'include',
          });
          if (response.ok) {
            const data = await response.json();
            const projectBoards = data.included?.boards || [];
            setBoardsByProject(prev => ({ ...prev, [projectId]: projectBoards }));
          }
        } catch (err) {
          console.error('Error fetching boards:', err);
        }
      },
      [accessToken]
    );

    const fetchListsForBoard = useCallback(
      async boardId => {
        if (!boardId) return;
        try {
          const response = await fetch(`${Config.SERVER_BASE_URL}/api/boards/${boardId}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
            credentials: 'include',
          });
          if (response.ok) {
            const data = await response.json();
            const boardLists = (data.included?.lists || []).sort((a, b) => a.position - b.position);
            setListsByBoard(prev => ({ ...prev, [boardId]: boardLists }));
          }
        } catch (err) {
          console.error('Error fetching lists:', err);
        }
      },
      [accessToken]
    );

    const updateTarget = useCallback((index, field, value) => {
      setBoardTargets(prev =>
        prev.map((t, i) => {
          if (i !== index) return t;
          const updated = { ...t, [field]: value };
          // Clear cascading selections
          if (field === 'targetProjectId') {
            updated.targetBoardId = '';
            updated.targetListId = '';
          }
          if (field === 'targetBoardId') {
            updated.targetListId = '';
          }
          return updated;
        })
      );
    }, []);

    const handleProjectChange = useCallback(
      (index, projectId) => {
        updateTarget(index, 'targetProjectId', projectId);
        if (projectId && !boardsByProject[projectId]) {
          fetchBoardsForProject(projectId);
        }
      },
      [updateTarget, boardsByProject, fetchBoardsForProject]
    );

    const handleBoardChange = useCallback(
      (index, boardId) => {
        updateTarget(index, 'targetBoardId', boardId);
        if (boardId && !listsByBoard[boardId]) {
          fetchListsForBoard(boardId);
        }
      },
      [updateTarget, listsByBoard, fetchListsForBoard]
    );

    const addTarget = useCallback(() => {
      setBoardTargets(prev => [...prev, { ...EMPTY_TARGET }]);
    }, []);

    const removeTarget = useCallback(index => {
      setBoardTargets(prev => {
        if (prev.length <= 1) return prev;
        return prev.filter((_, i) => i !== index);
      });
    }, []);

    const handleStatusToggle = useCallback(status => {
      setAllowedStatuses(prev => {
        if (prev.includes(status)) return prev.filter(s => s !== status);
        return [...prev, status];
      });
    }, []);

    const handleSave = useCallback(() => {
      onConfigUpdate({
        syncEnabled,
        syncFrequency,
        syncDateRange,
        onDuplicate,
        allowedStatuses,
        boardTargets,
        // Keep legacy fields pointing to first target for backward compatibility
        targetProjectId: boardTargets[0]?.targetProjectId || '',
        targetBoardId: boardTargets[0]?.targetBoardId || '',
        targetListId: boardTargets[0]?.targetListId || '',
        syncDateType: boardTargets[0]?.syncDateType || 'dropoff',
      });
    }, [
      syncEnabled,
      syncFrequency,
      syncDateRange,
      onDuplicate,
      allowedStatuses,
      boardTargets,
      onConfigUpdate,
    ]);

    const locationOptions = rcmLocations.map(loc => ({
      key: loc.id,
      value: String(loc.id),
      text: loc.name,
    }));

    return (
      <Segment>
        <Header as="h4" style={{ marginBottom: '15px' }}>
          <Icon name="sync" />
          <Header.Content>
            Sync Configuration
            <Header.Subheader>Control how and when bookings are synchronized</Header.Subheader>
          </Header.Content>
        </Header>

        <Form>
          <Form.Field>
            <Form.Checkbox
              toggle
              label="Enable Auto-Sync"
              checked={syncEnabled}
              onChange={(e, { checked }) => setSyncEnabled(checked)}
              disabled={isLoading}
            />
          </Form.Field>

          {syncEnabled && (
            <>
              <Form.Group widths="equal">
                <Form.Select
                  label="Frequency"
                  options={FREQUENCY_OPTIONS}
                  value={syncFrequency}
                  onChange={(e, { value }) => setSyncFrequency(value)}
                  disabled={isLoading}
                />
                <Form.Select
                  label="Date Range"
                  options={DATE_RANGE_OPTIONS}
                  value={syncDateRange}
                  onChange={(e, { value }) => setSyncDateRange(value)}
                  disabled={isLoading}
                />
                <Form.Select
                  label="On Duplicate"
                  options={DUPLICATE_OPTIONS}
                  value={onDuplicate}
                  onChange={(e, { value }) => setOnDuplicate(value)}
                  disabled={isLoading}
                />
              </Form.Group>
            </>
          )}

          <Header as="h5" style={{ marginTop: '20px', marginBottom: '10px' }}>
            <Icon name="target" />
            Board Targets
            <Header.Subheader>
              Each target syncs bookings to a specific board, filtered by date type and location
            </Header.Subheader>
          </Header>

          {boardTargets.map((target, index) => {
            const projectBoards = boardsByProject[target.targetProjectId] || [];
            const boardLists = listsByBoard[target.targetBoardId] || [];
            const targetNum = index + 1;

            return (
              <Segment
                key={index}
                style={{ backgroundColor: '#f9fafb', position: 'relative', paddingTop: '35px' }}
              >
                <Label
                  attached="top"
                  color="blue"
                  size="small"
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span>
                    Target {targetNum}
                    {target.label ? `: ${target.label}` : ''}
                  </span>
                  {boardTargets.length > 1 && (
                    <Icon
                      name="delete"
                      style={{ cursor: 'pointer', marginLeft: '8px' }}
                      onClick={() => removeTarget(index)}
                    />
                  )}
                </Label>

                <Form.Input
                  label="Label"
                  placeholder="e.g. Las Vegas Dropoffs"
                  value={target.label}
                  onChange={(e, { value }) => updateTarget(index, 'label', value)}
                  disabled={isLoading}
                  size="small"
                />

                <Form.Group widths="equal">
                  <Form.Select
                    label="Date Type"
                    options={DATE_TYPE_OPTIONS}
                    value={target.syncDateType}
                    onChange={(e, { value }) => updateTarget(index, 'syncDateType', value)}
                    disabled={isLoading}
                  />
                  <Form.Field>
                    <label>Location Filter</label>
                    <Dropdown
                      placeholder={
                        locationOptions.length > 0
                          ? 'All locations (no filter)'
                          : 'No locations cached'
                      }
                      fluid
                      multiple
                      selection
                      options={locationOptions}
                      value={target.filterLocationIds || []}
                      onChange={(e, { value }) => updateTarget(index, 'filterLocationIds', value)}
                      disabled={isLoading || locationOptions.length === 0}
                      noResultsMessage="No locations found"
                    />
                  </Form.Field>
                </Form.Group>

                <Form.Group widths="equal">
                  <Form.Select
                    label="Project"
                    placeholder="Select Project"
                    options={allProjects.map(p => ({ key: p.id, value: p.id, text: p.name }))}
                    value={target.targetProjectId}
                    onChange={(e, { value }) => handleProjectChange(index, value)}
                    disabled={isLoading}
                  />
                  <Form.Select
                    label="Board"
                    placeholder="Select Board"
                    options={projectBoards.map(b => ({ key: b.id, value: b.id, text: b.name }))}
                    value={target.targetBoardId}
                    onChange={(e, { value }) => handleBoardChange(index, value)}
                    disabled={isLoading || !target.targetProjectId}
                  />
                  <Form.Select
                    label="List"
                    placeholder="Select List"
                    options={boardLists.map(l => ({ key: l.id, value: l.id, text: l.name }))}
                    value={target.targetListId}
                    onChange={(e, { value }) => updateTarget(index, 'targetListId', value)}
                    disabled={isLoading || !target.targetBoardId}
                  />
                </Form.Group>
              </Segment>
            );
          })}

          <Button
            basic
            color="blue"
            size="small"
            icon="plus"
            content="Add Board Target"
            onClick={addTarget}
            disabled={isLoading}
            style={{ marginBottom: '15px' }}
          />

          {syncEnabled && (
            <>
              <Header as="h5" style={{ marginTop: '10px', marginBottom: '10px' }}>
                Status Filter
              </Header>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
                {ALL_STATUSES.map(status => (
                  <Checkbox
                    key={status}
                    label={status}
                    checked={allowedStatuses.includes(status)}
                    onChange={() => handleStatusToggle(status)}
                    disabled={isLoading}
                  />
                ))}
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
            <Button
              primary
              size="small"
              onClick={handleSave}
              loading={isLoading}
              disabled={isLoading}
              icon="save"
              content="Save Sync Settings"
            />
            {syncEnabled && (
              <Button
                color="blue"
                size="small"
                onClick={onAutoSyncNow}
                loading={isAutoSyncing}
                disabled={isLoading || isAutoSyncing}
                icon="play"
                content="Run Sync Now"
              />
            )}
          </div>

          <div style={{ marginTop: '8px' }}>
            <small style={{ color: '#666' }}>
              {boardTargets.length} board target{boardTargets.length !== 1 ? 's' : ''} configured
              {rcmLocations.length > 0 && ` · ${rcmLocations.length} locations available`}
            </small>
          </div>
        </Form>
      </Segment>
    );
  }
);

RcmSyncConfig.displayName = 'RcmSyncConfig';

export default RcmSyncConfig;
